import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, Edit2, Save, X, CheckCircle } from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import AttendanceDownloadPDF from '../common/AttendanceDownloadPDF';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrentCoordinates } from '../../utils/geolocation';
import { getCheckoutErrorMessage } from '../../utils/attendanceErrors';

const AttendanceCalendar = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDate, setEditingDate] = useState(null);
  const [approvedWfh, setApprovedWfh] = useState([]);
  const [editForm, setEditForm] = useState({
    checkInTime: '',
    checkOutTime: '',
    reason: ''
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchApprovedWfh = async () => {
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await api.get(`/employee/approved-wfh?month=${month}&year=${year}`);
      setApprovedWfh(response.data.wfhRequests || []);
    } catch (error) {
      console.error('Error fetching approved WFH:', error);
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // Company-wide attendance start date (days before this are "not applicable")
  const COMPANY_ATTENDANCE_START = new Date(2026, 3, 27, 0, 0, 0, 0); // 2026-04-27 (local)

  const startOfLocalDayClient = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(x.getTime())) return null;
    return new Date(x.getFullYear(), x.getMonth(), x.getDate(), 0, 0, 0, 0);
  };

  const getAttendanceEligibilityStartDate = () => {
    // Attendance should not be counted before the company start date.
    let start = COMPANY_ATTENDANCE_START;
    if (user && user.onboardingStatus === 'approved') {
      if (user.onboardingApprovedAt) {
        start = startOfLocalDayClient(user.onboardingApprovedAt) || start;
      } else if (user.joinDate) {
        start = startOfLocalDayClient(user.joinDate) || start;
      }
    }
    if (!start) return null;
    return start.getTime() < COMPANY_ATTENDANCE_START.getTime() ? COMPANY_ATTENDANCE_START : start;
  };

  const isBeforeAttendanceEligibility = (date) => {
    if (!date) return false;
    const start = getAttendanceEligibilityStartDate();
    if (!start) return false;
    return startOfLocalDayClient(date).getTime() < start.getTime();
  };

  const getWfhForDate = (date) => {
    if (!date) return null;
    if (isBeforeAttendanceEligibility(date)) return null;

    for (const wfh of approvedWfh) {
      const wfhDay = wfh.wfhDays?.find(day => {
        const dayDate = new Date(day.date);
        return dayDate.getFullYear() === date.getFullYear() &&
               dayDate.getMonth() === date.getMonth() &&
               dayDate.getDate() === date.getDate();
      });
      if (wfhDay) return wfhDay;
    }
    return null;
  };

  useEffect(() => {
    fetchMonthAttendance();
    fetchApprovedLeaves();
    fetchApprovedWfh(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  useEffect(() => {
    fetchTodayAttendance();
    fetchHolidays();
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      // Get today's date in IST
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istNow = new Date(now.getTime() + istOffset);
      const todayIST = istNow.toISOString().split('T')[0];
      
      // Fetch current month's attendance
      const month = istNow.getUTCMonth() + 1;
      const year = istNow.getUTCFullYear();
      
      const response = await api.get(`/employee/attendance/history?month=${month}&year=${year}`);
      
      // Find today's record by comparing dates in IST
      const todayRecord = response.data.attendances?.find(att => {
        const attDate = new Date(att.date);
        const attDateIST = new Date(attDate.getTime() + istOffset);
        const attDayIST = attDateIST.toISOString().split('T')[0];
        return attDayIST === todayIST;
      });
      
      setTodayAttendance(todayRecord || null);
    } catch (error) {
      console.error('Error fetching today attendance:', error);
      setTodayAttendance(null);
    }
  };

  const fetchHolidays = async () => {
    try {
      const response = await api.get('/employee/holidays');
      setHolidays(response.data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const fetchApprovedLeaves = async () => {
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await api.get(`/employee/approved-leaves?month=${month}&year=${year}`);
      setApprovedLeaves(response.data.leaves || []);
    } catch (error) {
      console.error('Error fetching approved leaves:', error);
    }
  };

  const getLeaveForDate = (date) => {
    if (!date) return null;
    if (isBeforeAttendanceEligibility(date)) return null;

    for (const leave of approvedLeaves) {
      // Check if this date falls within any leave period
      const leaveDay = leave.leaveDays?.find(day => {
        const dayDate = new Date(day.date);
        return dayDate.getFullYear() === date.getFullYear() &&
               dayDate.getMonth() === date.getMonth() &&
               dayDate.getDate() === date.getDate();
      });
      
      if (leaveDay) {
        return leaveDay;
      }
    }
    
    return null;
  };

  const isHoliday = (date) => {
    if (!date) return null;
    return holidays.find((h) => {
      const holidayDate = new Date(h.date);
      return (
        holidayDate.getFullYear() === date.getFullYear() &&
        holidayDate.getMonth() === date.getMonth() &&
        holidayDate.getDate() === date.getDate()
      );
    });
  };

  const isSunday = (date) => {
    if (!date) return false;
    return date.getDay() === 0;
  };

  // Recurring rule: the 2nd and 4th Saturday of every month are system week-offs for
  // HO (and anyone with no branch assigned). SSbranch employees work every Saturday -
  // no alternate week-off - matching utils/payrollCalculator.js on the backend.
  const isAltSaturdayHoliday = (date) => {
    if (!date) return false;
    if (user?.branch === 'SSbranch') return false;
    if (date.getDay() !== 6) return false;
    return Math.ceil(date.getDate() / 7) % 2 === 0;
  };

  const fetchMonthAttendance = async () => {
    try {
      setLoading(true);
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await api.get(`/employee/attendance/history?month=${month}&year=${year}`);
      
      // Group attendance by date
      const grouped = {};
      (response.data.attendances || []).forEach(att => {
        const attDate = new Date(att.date);
        const dateKey = `${attDate.getFullYear()}-${String(attDate.getMonth() + 1).padStart(2, '0')}-${String(attDate.getDate()).padStart(2, '0')}`;
        grouped[dateKey] = att;
      });
      
      setAttendanceData(grouped);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Failed to fetch attendance data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      await api.post('/employee/attendance/checkin');
      toast.success('Check-in successful!');
      await fetchTodayAttendance();
      await fetchMonthAttendance();
    } catch (error) {
      const message = error.response?.data?.message || 'Check-in failed';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      const coordinates = await getCurrentCoordinates();

      await api.post('/employee/attendance/checkout', coordinates);
      toast.success('Check-out successful!');
      await fetchTodayAttendance();
      await fetchMonthAttendance();
    } catch (error) {
      toast.error(getCheckoutErrorMessage(error));
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '--:--';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getDateAttendance = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;
    return attendanceData[dateKey];
  };

  const isToday = (date) => {
    if (!date) return false;
    // Compare dates in IST timezone
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istNow = new Date(now.getTime() + istOffset);
    
    return date.getDate() === istNow.getUTCDate() && 
           date.getMonth() === istNow.getUTCMonth() && 
           date.getFullYear() === istNow.getUTCFullYear();
  };

  const isFutureDate = (date) => {
    if (!date) return false;
    // Use IST timezone for today's date
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istNow = new Date(now.getTime() + istOffset);
    const todayIST = new Date(istNow.getUTCFullYear(), istNow.getUTCMonth(), istNow.getUTCDate());
    
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return compareDate > todayIST;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'full-day':
        return 'bg-green-100 border-green-300';
      case 'half-day':
        return 'bg-yellow-100 border-yellow-300';
      case 'absent':
        return 'bg-red-100 border-red-300';
      default:
        return 'bg-gray-200 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'full-day':
        return '✓';
      case 'half-day':
        return '½';
      case 'absent':
        return '✗';
      default:
        return '';
    }
  };

  const handleEditClick = (date, attendance) => {
    if (isFutureDate(date)) return;
    if (isBeforeAttendanceEligibility(date)) return;
    if (isHoliday(date) || isSunday(date) || isAltSaturdayHoliday(date)) return;
    if (getLeaveForDate(date)) return;
    if (getWfhForDate(date)) return;

    // Employee can only request modification of existing records, not create new ones
    // if (!attendance) {
    //   toast.info('No attendance record found for this date. You can only request modification of existing records.');
    //   return;
    // }

    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setEditingDate(dateKey);

    // Format times for time input (HH:MM)
    const formatTimeLocal = (dateString) => {
      if (!dateString) return '';
      const d = new Date(dateString);
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    setEditForm({
      checkInTime: attendance ? formatTimeLocal(attendance.checkInTime) : '',
      checkOutTime: attendance ? formatTimeLocal(attendance.checkOutTime) : '',
      reason: ''
    });

  };

  const handleCancelEdit = () => {
    setEditingDate(null);
    setEditForm({
      checkInTime: '',
      checkOutTime: '',
      reason: ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.checkInTime || !editForm.checkOutTime) {
      toast.error('Both check-in and check-out times are required');
      return;
    }

    const trimmedReason = editForm.reason.trim();
    if (!trimmedReason) {
      toast.error('Reason is required to submit a regularization request.');
      return;
    }
    if (trimmedReason.length > 500) {
      toast.error('Reason must be 500 characters or fewer.');
      return;
    }

    // Parse times and validate
    const [checkInHour, checkInMin] = editForm.checkInTime.split(':').map(Number);
    const [checkOutHour, checkOutMin] = editForm.checkOutTime.split(':').map(Number);

    const checkInMinutes = checkInHour * 60 + checkInMin;
    const checkOutMinutes = checkOutHour * 60 + checkOutMin;

    if (checkOutMinutes <= checkInMinutes) {
      toast.error('Check-out time must be after check-in time.');
      return;
    }

    // Client-side 60-day window check (fast feedback before API call)
    const selectedDate = new Date(editingDate);
    const now = new Date();
    const diffMs = now.getTime() - selectedDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays > 60) {
      toast.error('Regularization requests are only allowed for attendance within the last 60 days.');
      return;
    }

    const attendance = attendanceData[editingDate]; // may be undefined for absent days

    const checkInDateTime = new Date(selectedDate);
    checkInDateTime.setHours(checkInHour, checkInMin, 0, 0);

    const checkOutDateTime = new Date(selectedDate);
    checkOutDateTime.setHours(checkOutHour, checkOutMin, 0, 0);

    const payload = {
      requestedCheckIn: checkInDateTime.toISOString(),
      requestedCheckOut: checkOutDateTime.toISOString(),
      reason: trimmedReason
    };

    if (attendance) {
      payload.attendanceId = attendance._id;
    } else {
      payload.date = editingDate; // 'YYYY-MM-DD' — backend creates the attendance record on approval
    }

    setSavingEdit(true);
    try {
      await api.post('/employee/attendance/modification-request', payload);
      toast.success('Regularization request submitted. Awaiting admin approval.');
      await fetchTodayAttendance();
      await fetchMonthAttendance();
      handleCancelEdit();
    } catch (error) {
      console.error('Error submitting regularization request:', error);
      const backendMsg = error.response?.data?.message || '';
      // Map known backend messages to user-friendly text
      if (backendMsg.toLowerCase().includes('pending')) {
        toast.error('A regularization request is already pending for this date.');
      } else if (backendMsg.toLowerCase().includes('60')) {
        toast.error('Regularization requests are only allowed within the last 60 days.');
      } else if (backendMsg.toLowerCase().includes('not found')) {
        toast.error('Attendance record not found. It may have been removed.');
      } else if (backendMsg.toLowerCase().includes('after')) {
        toast.error('Check-out time must be after check-in time.');
      } else {
        toast.error(backendMsg || 'Failed to submit regularization request.');
      }
    } finally {
      setSavingEdit(false);
    }
  };

  // Build the badge values from the same day-level precedence that the calendar
  // renders: system paid day -> leave -> WFH -> attendance -> absent/future.
  // This prevents double-counting and makes the summary match the visible cells.
  const buildMonthlyStats = () => {
    const stats = {
      fullDay: 0,
      halfDay: 0,
      absent: 0,
      totalHours: 0,
      earnedLeaves: 0,
      sickLeaves: 0,
      casualLeaves: 0,
      compOffLeaves: 0,
      withoutPayLeaves: 0,
      wfhDays: 0,
    };
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();

    for (let day = 1; day <= lastDay; day += 1) {
      const date = new Date(year, month, day);
      if (isBeforeAttendanceEligibility(date)) continue;

      const attendance = getDateAttendance(date);
      const future = isFutureDate(date);
      const systemPaidDay =
        isSunday(date) || Boolean(isHoliday(date)) || isAltSaturdayHoliday(date);
      const leaveDay = systemPaidDay ? null : getLeaveForDate(date);
      const wfhDay = systemPaidDay || leaveDay ? null : getWfhForDate(date);

      // Total hours always comes from actual check-in/check-out records, never
      // from a synthesized holiday, leave, or WFH day.
      if (attendance && !future) {
        stats.totalHours += Number(attendance.totalHours) || 0;
      }

      if (systemPaidDay) {
        stats.fullDay += 1;
        continue;
      }

      if (leaveDay) {
        const units = leaveDay.dayType === 'half' ? 0.5 : 1;
        if (leaveDay.dayType === 'half') stats.halfDay += 1;
        if (leaveDay.leaveType === 'without_pay') {
          stats.withoutPayLeaves += units;
        } else {
          if (leaveDay.leaveType === 'earned') stats.earnedLeaves += units;
          if (leaveDay.leaveType === 'sick') stats.sickLeaves += units;
          if (leaveDay.leaveType === 'casual') stats.casualLeaves += units;
          if (leaveDay.leaveType === 'comp_off') stats.compOffLeaves += units;
          if (leaveDay.dayType !== 'half') stats.fullDay += 1;
        }
        continue;
      }

      if (wfhDay) {
        const units = wfhDay.dayType === 'half' ? 0.5 : 1;
        stats.wfhDays += units;
        if (wfhDay.dayType === 'half') stats.halfDay += 1;
        else stats.fullDay += 1;
        continue;
      }

      if (future) continue;

      if (attendance?.status === 'full-day') {
        stats.fullDay += 1;
      } else if (attendance?.status === 'half-day') {
        stats.halfDay += 1;
      } else {
        // A saved absent record and a blank past working day are both absent.
        stats.absent += 1;
      }
    }

    return {
      ...stats,
      totalHours: Math.round(stats.totalHours * 100) / 100,
    };
  };

  const monthlyStats = buildMonthlyStats();

  const days = getDaysInMonth(currentDate);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg shadow-lg p-4 text-white">
        <div className="flex items-center space-x-3">
          <Clock className="h-8 w-8" />
          <div>
            <h1 className="text-2xl font-bold">My Attendance</h1>
            <p className="text-indigo-100 text-sm">View and manage your attendance records</p>
          </div>
        </div>
      </div>

      {/* Monthly Statistics - Single Line */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <div className="bg-green-100 rounded-full p-2">
              <span className="text-lg">✓</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Full Days</p>
              <p className="text-lg font-bold text-green-700">{monthlyStats.fullDay}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="bg-yellow-100 rounded-full p-2">
              <span className="text-lg">½</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Half Days</p>
              <p className="text-lg font-bold text-yellow-700">{monthlyStats.halfDay}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="bg-red-100 rounded-full p-2">
              <span className="text-lg">✗</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Absents</p>
              <p className="text-lg font-bold text-red-700">{monthlyStats.absent}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="bg-blue-100 rounded-full p-2">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Hours</p>
              <p className="text-lg font-bold text-blue-700">{monthlyStats.totalHours.toFixed(1)}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="bg-emerald-100 rounded-full p-2">
              <span className="text-lg">🌴</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Earned</p>
              <p className="text-lg font-bold text-emerald-700">{monthlyStats.earnedLeaves}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="bg-pink-100 rounded-full p-2">
              <span className="text-lg">🏥</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Sick</p>
              <p className="text-lg font-bold text-pink-700">{monthlyStats.sickLeaves}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="bg-purple-100 rounded-full p-2">
              <span className="text-lg">☕</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Casual</p>
              <p className="text-lg font-bold text-purple-700">{monthlyStats.casualLeaves}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <div className="bg-amber-100 rounded-full p-2">
              <span className="text-lg">🎁</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Comp Off</p>
              <p className="text-lg font-bold text-amber-700">{monthlyStats.compOffLeaves}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="bg-orange-100 rounded-full p-2">
              <span className="text-lg">💼</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">W/O Pay</p>
              <p className="text-lg font-bold text-orange-700">{monthlyStats.withoutPayLeaves}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <div className="bg-cyan-100 rounded-full p-2">
              <span className="text-lg">🏠</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">WFH</p>
              <p className="text-lg font-bold text-cyan-700">{monthlyStats.wfhDays}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        {/* Month Navigation */}
        <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={handlePreviousMonth}
              className="p-1 hover:bg-indigo-400 rounded transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-white" />
            </button>
            
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5 text-white" />
              <h2 className="text-lg font-bold text-white">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
            </div>
            
            <button
              onClick={handleNextMonth}
              className="p-1 hover:bg-indigo-400 rounded transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="p-3">
          {loading ? (
            <LoadingSpinner size="lg" className="py-8" />
          ) : (
            <>

          <div className="overflow-x-auto">
            <div className="min-w-[980px]">
              
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {dayNames.map((day) => (
                  <div
                    key={day}
                    className="text-center font-semibold text-gray-600 text-xs py-1"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((date, index) => {
                  const beforeActive = date && isBeforeAttendanceEligibility(date);
                  const attendance = beforeActive ? null : getDateAttendance(date);
                  const today = isToday(date);
                  const future = date && !beforeActive && isFutureDate(date);
                  const holidayInfo = beforeActive ? null : isHoliday(date);
                  // 2nd/4th Saturday of the month is folded into `sunday` itself (not a
                  // separate flag) - it's meant to render and behave identically: auto
                  // full-paid day, excluded from attendance/leave the same way Sunday is.
                  const sunday = beforeActive ? false : (isSunday(date) || isAltSaturdayHoliday(date));
                  // System-paid days have precedence. A leave or WFH record on a
                  // Sunday/holiday must not add a second status to the same cell.
                  const leaveDay = holidayInfo || sunday ? null : getLeaveForDate(date);
                  const wfhDay = !holidayInfo && !sunday && !leaveDay ? getWfhForDate(date) : null;
                  const dateKey = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : null;
                  const isEditing = dateKey === editingDate;

                  return (
                    <div
                      key={index}
                      title={beforeActive ? 'Not active yet' : undefined}
                      className={`
                        min-h-20 p-2 rounded-lg border transition-all duration-200
                        ${!date ? 'bg-gray-50 border-transparent cursor-default' : ''}
                        ${beforeActive ? 'bg-gray-100 border-gray-300 border-dashed cursor-not-allowed opacity-80' : ''}
                        ${future && !holidayInfo && !sunday ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-40' : ''}
                        ${today && !future ? `border-indigo-400 border-2 ${beforeActive ? 'bg-gray-100' : 'bg-indigo-50'}` : ''}
                        ${holidayInfo && !today ? 'bg-sky-100 border-sky-300' : ''}
                        ${sunday && !holidayInfo && !today && !future ? 'bg-green-100 border-green-400' : ''}
                        ${leaveDay && !today && !holidayInfo && !sunday ? (
                          leaveDay.leaveType === 'earned' 
                            ? (leaveDay.dayType === 'half' ? 'bg-green-50 border-green-300' : 'bg-green-100 border-green-400')
                            : leaveDay.leaveType === 'sick'
                            ? (leaveDay.dayType === 'half' ? 'bg-pink-50 border-pink-300' : 'bg-pink-100 border-pink-400')
                            : leaveDay.leaveType === 'comp_off'
                            ? (leaveDay.dayType === 'half' ? 'bg-amber-50 border-amber-300' : 'bg-amber-100 border-amber-400')
                            : (leaveDay.dayType === 'half' ? 'bg-orange-50 border-orange-300' : 'bg-orange-100 border-orange-400')
                        ) : ''}
                        ${wfhDay && !today && !holidayInfo && !sunday ? (
                          wfhDay.dayType === 'half' ? 'bg-cyan-50 border-cyan-300' : 'bg-cyan-100 border-cyan-400'
                        ) : ''}
                        ${attendance && !today && !future && !holidayInfo && !sunday && !leaveDay && !wfhDay ? `${getStatusColor(attendance.status)}` : ''}
                        ${!attendance && date && !today && !future && !holidayInfo && !sunday && !leaveDay && !wfhDay && !beforeActive ? 'bg-red-50 border-red-200' : ''}
                        ${isEditing ? 'ring-2 ring-blue-400 scale-105 z-10' : ''}
                      `}
                    >
                      {date && (
                        <div className="h-full flex flex-col">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`
                              text-sm font-bold
                              ${today ? 'text-indigo-700' : ''}
                              ${beforeActive && !today ? 'text-gray-500' : ''}
                              ${holidayInfo && !today ? 'text-sky-700' : ''}
                              ${sunday && !holidayInfo && !today ? 'text-green-700' : ''}
                              ${leaveDay && !today && !holidayInfo && !sunday ? (leaveDay.leaveType === 'earned' ? 'text-green-700' : leaveDay.leaveType === 'sick' ? 'text-pink-700' : leaveDay.leaveType === 'comp_off' ? 'text-amber-700' : leaveDay.leaveType === 'casual' ? 'text-purple-700' : 'text-orange-700') : ''}
                              ${wfhDay && !today && !holidayInfo && !sunday ? 'text-cyan-700' : ''}
                              ${attendance && !today && !holidayInfo && !sunday && !leaveDay && !wfhDay ? 'text-gray-700' : ''}
                              ${!attendance && !today && !holidayInfo && !sunday && !future && !leaveDay && !wfhDay && !beforeActive ? 'text-gray-700' : ''}
                              ${future && !holidayInfo && !sunday ? 'text-gray-400' : ''}
                            `}>
                              {date.getDate()}
                            </span>
                            <div className="flex items-center space-x-1">
                              {today && (
                                <span className="text-xs font-semibold text-indigo-700 bg-indigo-200 px-1.5 py-0.5 rounded">
                                  Today
                                </span>
                              )}
                              {sunday && !holidayInfo && !leaveDay && !wfhDay && (
                                <span className="text-base font-bold text-green-600">✓</span>
                              )}
                              {holidayInfo && !leaveDay && !wfhDay && (
                                <span className="text-base font-bold text-sky-600">✓</span>
                              )}
                              {attendance && !leaveDay && !wfhDay && !sunday && (
                                <span className={`text-base font-bold ${
                                  attendance.status === 'full-day' ? 'text-green-600' :
                                  attendance.status === 'half-day' ? 'text-yellow-600' :
                                  'text-red-600'
                                }`}>
                                  {getStatusIcon(attendance.status)}
                                </span>
                              )}
                              {wfhDay && !leaveDay && !sunday && (
                                <span className="text-base font-bold text-cyan-700">
                                  {wfhDay.dayType === 'half' ? '½' : '✓'}
                                </span>
                              )}
                                {!future && !isEditing && !beforeActive && !holidayInfo && !sunday && !leaveDay && !wfhDay && (
                                  <button
                                    onClick={() => handleEditClick(date, attendance)}
                                    className={`p-0.5 rounded hover:bg-opacity-20 transition-colors ${
                                      !attendance ? 'hover:bg-red-200 text-red-600' :
                                      'hover:bg-gray-200 text-gray-600'
                                    }`}
                                    title="Request attendance modification"
                                  >
                                    <Edit2 className="h-3 w-3" />
                                  </button>
                                )}
                            </div>
                          </div>

                          {/* Holiday Badge — holidays are paid full days */}
                          {holidayInfo && (
                            <div className="mb-1 bg-sky-200 border border-sky-400 rounded px-1.5 py-0.5">
                              <div className="text-xs font-semibold text-sky-900 text-center truncate">
                                {holidayInfo.description}
                              </div>
                              <div className="text-xs font-semibold text-sky-900 text-center truncate">
                                Full Day
                              </div>
                            </div>
                          )}

                          {/* Sunday or 2nd/4th Saturday = Full Day */}
                          {sunday && !holidayInfo && (
                            <div className="mb-1 bg-green-200 border border-green-400 rounded px-1.5 py-0.5">
                              <div className="text-xs font-semibold text-green-900 text-center truncate">
                                Full Day
                              </div>
                            </div>
                          )}

                          {/* Leave Badge */}
                          {leaveDay && !sunday && (
                            <div className={`mb-1 rounded px-1.5 py-0.5 border ${
                              leaveDay.leaveType === 'earned'
                                ? 'bg-green-200 border-green-400'
                                : leaveDay.leaveType === 'sick'
                                ? 'bg-pink-200 border-pink-400'
                                : leaveDay.leaveType === 'comp_off'
                                ? 'bg-amber-200 border-amber-400'
                                : leaveDay.leaveType === 'casual'
                                ? 'bg-purple-200 border-purple-400'
                                : 'bg-orange-200 border-orange-400'
                            }`}>
                              <div className={`text-xs font-semibold text-center truncate ${
                                leaveDay.leaveType === 'earned' ? 'text-green-900' : leaveDay.leaveType === 'sick' ? 'text-pink-900' : leaveDay.leaveType === 'comp_off' ? 'text-amber-900' : leaveDay.leaveType === 'casual' ? 'text-purple-900' : 'text-orange-900'
                              }`}>
                                {leaveDay.dayType === 'half' ? '½ ' : ''}{leaveDay.leaveType === 'earned' ? 'Earned' : leaveDay.leaveType === 'sick' ? 'Sick' : leaveDay.leaveType === 'comp_off' ? 'Comp Off' : leaveDay.leaveType === 'casual' ? 'Casual' : 'W/O Pay'}
                              </div>
                            </div>
                          )}

                          {wfhDay && !leaveDay && !sunday && (
                            <div className="mb-1 rounded px-1.5 py-0.5 border bg-cyan-200 border-cyan-400">
                              <div className="text-xs font-semibold text-center truncate text-cyan-900">
                                {wfhDay.dayType === 'half' ? '½ ' : ''}WFH
                              </div>
                            </div>
                          )}

                          {!isEditing ? (
                            <>
                              {attendance && !future && (
                                <div className="space-y-0.5 text-xs">
                                  <div className="bg-white bg-opacity-70 rounded px-1.5 py-0.5">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">In</span>
                                      <span className="font-bold text-gray-900">
                                        {new Date(attendance.checkInTime).toLocaleTimeString('en-US', {
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                    </div>
                                  </div>
                                  {attendance.checkOutTime && (
                                    <div className="bg-white bg-opacity-70 rounded px-1.5 py-0.5">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Out</span>
                                        <span className="font-bold text-gray-900">
                                          {new Date(attendance.checkOutTime).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  {attendance.totalHours > 0 && (
                                    <div className="bg-white bg-opacity-70 rounded px-1.5 py-0.5">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Hrs</span>
                                        <span className="font-bold text-gray-900">
                                          {attendance.totalHours.toFixed(1)}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {!attendance && !future && !holidayInfo && !sunday && !leaveDay && !wfhDay && !beforeActive && (
                                <div className="flex-1 flex flex-col items-center justify-center space-y-1">
                                  <span className="text-lg font-bold text-red-600">✗</span>
                                  <span className="text-xs font-semibold text-red-600">Absent</span>
                                </div>
                              )}

                              {beforeActive && (
                                <div className="flex-1 flex items-center justify-center">
                                  <span className="text-xs text-gray-500">—</span>
                                </div>
                              )}

                              {future && !holidayInfo && !sunday && (
                                <div className="flex-1 flex items-center justify-center">
                                  <span className="text-xs text-gray-400">Future</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="space-y-1">
                              {/* Approval Required badge */}
                              <div className="flex items-center justify-center">
                                <span className="text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
                                  Approval Required
                                </span>
                              </div>
                              <div className="text-xs">
                                <label className="block text-gray-700 font-semibold mb-0.5">In</label>
                                <input
                                  type="time"
                                  value={editForm.checkInTime}
                                  onChange={(e) => setEditForm({ ...editForm, checkInTime: e.target.value })}
                                  className="w-full px-1.5 py-1 text-xs border rounded focus:ring-1 focus:ring-indigo-500"
                                />
                              </div>
                              <div className="text-xs">
                                <label className="block text-gray-700 font-semibold mb-0.5">Out</label>
                                <input
                                  type="time"
                                  value={editForm.checkOutTime}
                                  onChange={(e) => setEditForm({ ...editForm, checkOutTime: e.target.value })}
                                  className="w-full px-1.5 py-1 text-xs border rounded focus:ring-1 focus:ring-indigo-500"
                                />
                              </div>
                              <div className="text-xs">
                                <label className="block text-gray-700 font-semibold mb-0.5">Reason *</label>
                                <textarea
                                  value={editForm.reason}
                                  onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                                  maxLength={500}
                                  rows={2}
                                  placeholder="Why are you requesting this change?"
                                  className="w-full px-1.5 py-1 text-xs border rounded focus:ring-1 focus:ring-indigo-500 resize-none"
                                />
                                <p className="text-right text-gray-400" style={{ fontSize: '0.6rem' }}>
                                  {editForm.reason.length}/500
                                </p>
                              </div>
                              <p className="text-center text-gray-500" style={{ fontSize: '0.6rem' }}>
                                Request sent to admin for approval.
                              </p>
                              {/* <div className="flex space-x-1 pt-0.5">
                                <button
                                  onClick={handleSaveEdit}
                                  disabled={savingEdit}
                                  className="flex-1 flex items-center justify-center px-1.5 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50"
                                >
                                  {savingEdit ? (
                                    <LoadingSpinner size="sm" />
                                  ) : (
                                    <>
                                      <Save className="h-3 w-3 mr-0.5" />
                                      Request Change
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  disabled={savingEdit}
                                  className="flex-1 flex items-center justify-center px-1.5 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 disabled:opacity-50"
                                >
                                  <X className="h-3 w-3 mr-0.5" />
                                  Cancel
                                </button>
                              </div> */}
                              <div className="flex flex-col space-y-1 pt-0.5">
                                  <button
                                    onClick={handleSaveEdit}
                                    disabled={savingEdit}
                                    className="w-full flex items-center justify-center px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50"
                                  >
                                    {savingEdit ? (
                                      <LoadingSpinner size="sm" />
                                    ) : (
                                      <>
                                        <Save className="h-3 w-3 mr-1 flex-shrink-0" />
                                        Request Change
                                      </>
                                    )}
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    disabled={savingEdit}
                                    className="w-full flex items-center justify-center px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 disabled:opacity-50"
                                  >
                                    <X className="h-3 w-3 mr-1 flex-shrink-0" />
                                    Cancel
                                  </button>
                                </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </div>
              </div>
            </>
          )}
        </div>

        {/* Legend */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="font-semibold text-gray-700">Legend:</span>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded bg-green-100 border border-green-300"></div>
              <span className="text-gray-600">Full Day</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300"></div>
              <span className="text-gray-600">Half Day</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded bg-red-100 border border-red-300"></div>
              <span className="text-gray-600">Absent</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded bg-sky-100 border border-sky-300"></div>
              <span className="text-gray-600">Holiday</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded bg-indigo-50 border-2 border-indigo-400"></div>
              <span className="text-gray-600">Today</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded bg-cyan-100 border border-cyan-400"></div>
              <span className="text-gray-600">WFH (Present)</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-gray-600"></span>
            </div>
          </div>
        </div>
      </div>

      {/* Today's Attendance Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-indigo-100">
          <h3 className="text-base font-bold text-gray-900 flex items-center">
            <Clock className="h-4 w-4 mr-2 text-indigo-600" />
            Today's Attendance - {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </h3>
        </div>
        <div className="p-4">
          {loading ? (
            <LoadingSpinner size="md" />
          ) : (
            <>
              {todayAttendance ? (
                <div className="space-y-3">
                  {/* Time Information */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                      <div className="text-xs text-green-600 font-medium mb-1">Check In</div>
                      <div className="text-xl font-bold text-green-700">
                        {formatTime(todayAttendance.checkInTime)}
                      </div>
                    </div>
                    
                    <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                      <div className="text-xs text-red-600 font-medium mb-1">Check Out</div>
                      <div className="text-xl font-bold text-red-700">
                        {formatTime(todayAttendance.checkOutTime)}
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <div className="text-xs text-blue-600 font-medium mb-1">Total Hours</div>
                      <div className="text-xl font-bold text-blue-700">
                        {todayAttendance.totalHours ? `${todayAttendance.totalHours.toFixed(1)}h` : '--'}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-center space-x-3 pt-1">
                    {!todayAttendance.checkInTime && (
                      <button
                        onClick={handleCheckIn}
                        disabled={actionLoading}
                        className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-md hover:shadow-lg transition-all"
                      >
                        {actionLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            <Clock className="h-4 w-4 mr-2" />
                            Check In Now
                          </>
                        )}
                      </button>
                    )}

                    {todayAttendance.checkInTime && !todayAttendance.checkOutTime && (
                      <button
                        onClick={handleCheckOut}
                        disabled={actionLoading}
                        className="flex items-center px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-md hover:shadow-lg transition-all"
                      >
                        {actionLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            <Clock className="h-4 w-4 mr-2" />
                            Check Out Now
                          </>
                        )}
                      </button>
                    )}

                    {todayAttendance.checkInTime && todayAttendance.checkOutTime && (
                      <div className="text-center bg-green-50 rounded-lg px-5 py-3 border border-green-200">
                        <CheckCircle className="h-6 w-6 mx-auto mb-1 text-green-600" />
                        <p className="font-semibold text-green-700 text-sm">Attendance completed for today</p>
                        <p className="text-xs text-green-600 mt-0.5">
                          Status: <span className="font-bold capitalize">{todayAttendance.status.replace('-', ' ')}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Clock className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-3 font-medium">No attendance record for today</p>
                  <button
                    onClick={handleCheckIn}
                    disabled={actionLoading}
                    className="flex items-center mx-auto px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-md hover:shadow-lg transition-all"
                  >
                    {actionLoading ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <Clock className="h-4 w-4 mr-2" />
                        Check In Now
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* PDF Download Section */}
      {user && <AttendanceDownloadPDF isAdmin={false} employeeData={user} />}

      {/* Attendance Rules */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <h4 className="text-base font-medium text-indigo-900 mb-2">📋 Attendance Rules & Tips</h4>
        <ul className="space-y-1 text-xs text-indigo-800">
          <li>• <strong>Full Day:</strong> 9+ hours of work</li>
          <li>• <strong>Half Day:</strong> 5 to 8.9 hours of work</li>
          <li>• <strong>Absent:</strong> Less than 5 hours of work</li>
          <li>• <strong>Special Rule:</strong> 3 consecutive half days in a month = 2 half days + 1 absent</li>
          <li>• Attendance changes require admin approval — click the <Edit2 className="h-3 w-3 inline" /> icon on a recorded day to submit a request.</li>
          <li>• You can only request changes for existing attendance records from the last 60 days.</li>
        </ul>
      </div>
    </div>
  );
};

export default AttendanceCalendar;
